import { useEffect, useCallback } from "react";
import {
  requestPermissionAndGetToken,
  saveTokenToFirestore,
  onMessageListener,
} from "../firebaseMessaging";
import { auth } from "../firebase";

/**
 * Hook para gestionar notificaciones push:
 * - Solicita permisos y guarda el token FCM en Firestore.
 * - Escucha notificaciones en primer plano.
 * - Permite enviar notificaciones desde el frontend al backend.
 *
 * @param {string} userId - ID del usuario autenticado (usado para guardar el token en Firestore).
 * @param {function} onMessageCallback - Callback opcional para manejar notificaciones recibidas en primer plano.
 * @returns {object} { sendNotification } - Función para enviar una notificación desde el cliente.
 */
const useNotifications = (userId, onMessageCallback) => {
  useEffect(() => {
    if (!userId) return;

    const init = async () => {
      try {
        const token = await requestPermissionAndGetToken();
        if (token) {
          await saveTokenToFirestore(userId, token);
        }
      } catch (error) {
        console.error("Error obteniendo o guardando el token:", error);
      }
    };

    init();

    const unsubscribe = onMessageListener((payload) => {
      if (onMessageCallback) onMessageCallback(payload);
    });

    return () => {
      unsubscribe && unsubscribe();
    };
  }, [userId, onMessageCallback]);

  const sendNotification = useCallback(async ({ authorId, title, body, tweetId }) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${backendUrl}/api/send-tweet-notification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({ authorId, title, body, tweetId }),
      });

      if (!response.ok) {
        console.error("Error del servidor al enviar notificación:", await response.text());
      }
    } catch (error) {
      console.error("Error al enviar la notificación:", error);
    }
  }, []);

  return { sendNotification };
};

export default useNotifications;
